import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface ConversationParticipant {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  company_id: string;
  company_name: string | null;
}

export interface CompanyConversationSummary {
  company_id: string;
  company_name: string;
  last_message_id: string;
  last_message: string;
  last_message_at: string;
  last_sender_user_id: string;
  last_sender_company_id: string;
  last_sender_name: string | null;
  unread_count: number;
  marked_unread: boolean;
  participants: ConversationParticipant[];
}

export interface CompanyConversationMessage {
  id: string;
  sender_user_id: string;
  sender_company_id: string;
  recipient_company_id: string;
  listing_id: string | null;
  parent_message_id: string | null;
  content: string;
  is_read: boolean;
  created_at: string;
  delivery_source: string | null;
  sender_name: string | null;
  sender_email: string | null;
  sender_phone: string | null;
  sender_company_name: string | null;
  listing_title: string | null;
}

export function useCompanyConversations(enabled = true) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['company-conversations', user?.id],
    enabled: enabled && !!user,
    staleTime: 15_000,
    queryFn: async (): Promise<CompanyConversationSummary[]> => {
      const { data, error } = await supabase.rpc('list_company_conversations', {
        p_limit: 100,
        p_before: null,
      });
      if (error) throw error;
      return (data as unknown as CompanyConversationSummary[]) ?? [];
    },
  });
}

export async function loadCompanyConversation(
  counterpartyCompanyId: string,
  before?: { createdAt: string; id: string },
): Promise<CompanyConversationMessage[]> {
  const { data, error } = await supabase.rpc('get_company_conversation_messages', {
    p_counterparty_company_id: counterpartyCompanyId,
    p_limit: 50,
    p_before_created_at: before?.createdAt ?? null,
    p_before_id: before?.id ?? null,
  });
  if (error) throw error;
  return (data as unknown as CompanyConversationMessage[]) ?? [];
}

export async function setCompanyConversationUnread(counterpartyCompanyId: string, unread: boolean) {
  const { error } = await supabase.rpc('set_company_conversation_unread', {
    p_counterparty_company_id: counterpartyCompanyId,
    p_unread: unread,
  });
  if (error) throw error;
}